"use client";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { showGlobalLoading, hideGlobalLoading, setGlobalLoading } from "@/store/slices/ui.slice";
import { useCallback } from "react";

/**
 * Hook to control global loading state
 * 
 * @example
 * const { showLoading, hideLoading, isLoading } = useGlobalLoading();
 * 
 * // Show loading with default message
 * showLoading();
 * 
 * // Show loading with custom message
 * showLoading("Saving category...");
 * 
 * // Hide loading
 * hideLoading();
 * 
 * // Check if loading
 * if (isLoading) {
 *   // Loading is active
 * }
 */
export const useGlobalLoading = () => {
  const dispatch = useAppDispatch();
  const globalLoading = useAppSelector((state) => state.ui.globalLoading);

  const showLoading = useCallback((message?: string) => {
    dispatch(showGlobalLoading(message));
  }, [dispatch]);

  const hideLoading = useCallback(() => {
    dispatch(hideGlobalLoading());
  }, [dispatch]);

  const setLoading = useCallback((isLoading: boolean, message?: string) => {
    dispatch(setGlobalLoading({ isLoading, message }));
  }, [dispatch]);

  return {
    /**
     * Show global loading overlay
     * @param message Optional loading message
     */
    showLoading,
    /**
     * Hide global loading overlay
     */
    hideLoading,
    /**
     * Set loading state with message
     * @param isLoading Whether to show loading
     * @param message Optional loading message
     */
    setLoading,
    /**
     * Current loading state
     */
    isLoading: globalLoading.isLoading,
    /**
     * Current loading message
     */
    message: globalLoading.message,
    /**
     * Full loading state object
     */
    loadingState: globalLoading,
  };
};